import './App.css';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';


function ScrollToHash() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (pathname !== "/" || !hash) return;
    const id = hash.slice(1);

    const scroll = () =>
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

    // wait for the home page to render, then re-check once in case images shifted the layout
    const t1 = setTimeout(scroll, 100);
    const t2 = setTimeout(scroll, 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [hash, pathname]);

  return null;
}






const SECTION_IDS = ["home", "aoe-section", "services", "partners"];

function useActiveSection(sectionIds) {
  const [active, setActive] = useState("");

  useEffect(() => {
    const LINE = 120; // px from top of viewport, just below the fixed header

    const update = () => {
      let current = "";
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= LINE && rect.bottom > LINE) {
          current = id;
          break;
        }
      }
      setActive(current);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [sectionIds]);

  return active;
}

function Header() {
  const [atTop, setAtTop] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const activeSection = useActiveSection(SECTION_IDS);

  useEffect(() => {
    const handleScroll = () => setAtTop(window.scrollY === 0);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navButtonClass = (id) => `
    group relative px-2 py-1 transition-all duration-300 bg-transparent
    ${activeSection !== id ? 'hover:-translate-y-1' : ''}
    after:content-[''] after:absolute after:left-1/2 after:translate-x-[-50%] after:-bottom-0
    after:h-1 after:bg-[#004ED1] after:transition-all after:duration-300
    ${activeSection === id ? 'after:w-14' : 'after:w-0'}
  `;

  return (
    <div className={`fixed top-0 left-0 w-full z-50 ${atTop ? '' : 'shadow-md'}`}>
      <header className="flex items-center justify-between bg-white px-5 py-3 md:px-10">
        <div>
          <a href="/">
            <img src="/assets/logo.svg" className="h-12 md:h-16" alt="Logo" />
          </a>
        </div>

        {/* Hamburger for mobile */}
        <div className="md:hidden">
          <button onClick={() => setMenuOpen(!menuOpen)}>
            <span className="text-3xl font-bold">&#9776;</span>
          </button>
        </div>

        {/* Desktop menu */}
        <nav className="hidden md:flex gap-5">
          <button onClick={() => document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' })} className={navButtonClass("home")}>HOME</button>
          <button onClick={() => document.getElementById('aoe-section')?.scrollIntoView({ behavior: 'smooth' })} className={navButtonClass("aoe-section")}>EXPERTISE</button>
          <button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className={navButtonClass("services")}>SERVICES</button>
          <button onClick={() => document.getElementById('partners')?.scrollIntoView({ behavior: 'smooth' })} className={navButtonClass("partners")}>PARTNERS & CLIENTS</button>
          <button onClick={() => window.location.href = "/insights"} className="group relative bg-transparent transition-all duration-300 hover:-translate-y-1">INSIGHTS</button>
          <button onClick={() => window.location.href = "/contact"} className="group relative bg-transparent transition-all duration-300 hover:-translate-y-1">CONTACT</button>
        </nav>
      </header>

      {/* Mobile menu */}
      {menuOpen && (
        <nav className="md:hidden bg-white shadow-md flex flex-col gap-2 px-5 py-3">
          <button onClick={() => { setMenuOpen(false); document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' }) }}>HOME</button>
          <button onClick={() => { setMenuOpen(false); document.getElementById('aoe-section')?.scrollIntoView({ behavior: 'smooth' }) }}>EXPERTISE</button>
          <button onClick={() => { setMenuOpen(false); document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }) }}>SERVICES</button>
          <button onClick={() => { setMenuOpen(false); document.getElementById('partners')?.scrollIntoView({ behavior: 'smooth' }) }}>PARTNERS & CLIENTS</button>
          <button onClick={() => { setMenuOpen(false); window.location.href = "/insights" }}>INSIGHTS</button>
          <button onClick={() => { setMenuOpen(false); window.location.href = "/contact" }}>CONTACT</button>
        </nav>
      )}
    </div>
  );
}


function Header_stranger() {
  const [atTop, setAtTop] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const currentPath = location.pathname;

  useEffect(() => {
    const handleScroll = () => setAtTop(window.scrollY === 0);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Responsive underline style
  const activeUnderline = `
    after:content-[''] 
    after:absolute 
    after:left-1/2 
    after:translate-x-[-50%] 
    after:bottom-0 
    after:h-1 
    after:bg-[#004ED1] 
    after:transition-all 
    after:duration-300
    after:w-14
  `;

  const navButtonClass = (isActive) => `
    group relative px-2 py-1 transition-all duration-300 bg-transparent
    ${!isActive ? "hover:-translate-y-1" : ""}
  `;

  const handleNavClick = (path) => {
    navigate(path);
    setMenuOpen(false); // Close mobile menu after clicking
  };

  // Links back to sections on the home page (scrolled to by ScrollToHash)
  const sectionLinks = [
    { label: "HOME", path: "/#home" },
    { label: "EXPERTISE", path: "/#aoe-section" },
    { label: "SERVICES", path: "/#services" },
    { label: "PARTNERS & CLIENTS", path: "/#partners" },
  ];

  return (
    <div
      className={`fixed top-0 left-0 w-full z-50 overflow-visible ${
        atTop ? "" : "shadow-md"
      }`}
    >
      <header
        className={`flex items-center justify-between bg-white px-5 py-3 md:px-10 transition-all duration-300 ${
          atTop ? "top-0" : "-top-[80px] group-hover:top-0 pointer-events-auto"
        }`}
      >
        <div className="pr-5 md:pr-20">
          <a href="/">
            <img src="/assets/logo.svg" className="h-12 md:h-16" alt="Logo" />
          </a>
        </div>

        {/* Hamburger menu button for mobile */}
        <div className="md:hidden">
          <button onClick={() => setMenuOpen(!menuOpen)}>
            <span className="text-3xl font-bold">&#9776;</span>
          </button>
        </div>

        {/* Desktop menu */}
        <nav className="hidden md:flex gap-7">
          {sectionLinks.map((link) => (
            <button
              key={link.path}
              onClick={() => handleNavClick(link.path)}
              className={navButtonClass(false)}
            >
              {link.label}
            </button>
          ))}

          <button
            onClick={() => handleNavClick("/insights")}
            className={`${navButtonClass(currentPath === "/insights")} ${currentPath === "/insights" ? activeUnderline : ""}`}
          >
            INSIGHTS
          </button>
          <button
            onClick={() => handleNavClick("/contact")}
            className={`${navButtonClass(currentPath === "/contact")} ${currentPath === "/contact" ? activeUnderline : ""}`}
          >
            CONTACT
          </button>
        </nav>
      </header>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <nav className="md:hidden bg-white shadow-md flex flex-col gap-2 px-5 py-3">
          {sectionLinks.map((link) => (
            <button key={link.path} onClick={() => handleNavClick(link.path)}>
              {link.label}
            </button>
          ))}

          <button
            onClick={() => handleNavClick("/insights")}
            className={currentPath === "/insights" ? "font-bold text-[#004ED1]" : ""}
          >
            INSIGHTS
          </button>
          <button
            onClick={() => handleNavClick("/contact")}
            className={currentPath === "/contact" ? "font-bold text-[#004ED1]" : ""}
          >
            CONTACT
          </button>
        </nav>
      )}
    </div>
  );
}

function Banner() {
  const images = [
    { src: "/assets/espen.jpg", position: "object-[center_20%]" },
    { src: "/assets/Forsidebilde.jpg", position: "object-center" },
  ]

  const [offset, setOffset] = useState(0)
  const [current, setCurrent] = useState(0)

  // Parallax scroll
  useEffect(() => {
    const handleScroll = () => {
      setOffset(window.scrollY * 0.5)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Auto-slide (resets when current changes)
  useEffect(() => {
    if (images.length < 2) return

    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length)
    }, 10 * 1000)

    return () => clearInterval(interval)
  }, [current])

  const nextSlide = () => {
    setCurrent((prev) => (prev + 1) % images.length)
  }

  const prevSlide = () => {
    setCurrent((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    )
  }

  return (
    <div
      id="home"
      className="relative overflow-hidden h-[560px] xl:h-[850px]"
    >
      {images.map((img, index) => (
        <img
          key={img.src}
          src={img.src}
          className={`absolute top-20 left-0 w-full h-full object-cover ${img.position} brightness-75
                      transition-opacity duration-1000 ease-in-out
                      ${index === current ? "opacity-100" : "opacity-0"}`}
          style={{
            transform: `translateY(${offset}px)`
          }}
        />
      ))}

      {/* Navigation Buttons */}
      <button
        onClick={prevSlide}
        className="absolute left-5 top-1/2 z-20
                   bg-transparent text-white px-4 py-2
                   transition-all duration-300 text-7xl hover:scale-125 origin-center hidden sm:block active:text-gray-300"
      >
        ‹
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-5 top-1/2 z-20
                   bg-transparent text-white px-4 py-2
                   transition-all duration-300 text-7xl hover:scale-125 origin-center hidden sm:block active:text-gray-300"
      >
        ›
      </button>

      <img
        src="/assets/Decorator_1.svg"
        className="absolute object-cover -top-12 z-10"
      />

      <div
        className="ml-3 absolute top-1/2 left-1/2 -translate-x-1/2 w-full z-10
                   text-white text-center text-2xl"
      >
        <h1 className="font-bold">
          World leading expert in Subsea Hydraulic Control Systems
        </h1>
        <h2>Engineering Consultancy Services</h2>

        <button
          onClick={() => (window.location.href = "/contact")}
          className="font-bold px-10 py-2 mt-12 bg-[#032866]
                     border-[#001B49] border
                     transition-all duration-300
                     hover:bg-[#001B49]
                     hover:-translate-y-1 hover:shadow-md"
        >
          CONTACT
        </button>
      </div>
    </div>
  )
}


function AreaEx({ img_path, t }) {
  return (
    <div className="relative transition-all duration-500 w-full max-w-sm mx-auto 
    transform hover:scale-105 active:scale-110 cursor-pointer">
      <img src={img_path} alt="" className="w-full h-auto rounded-lg" />

      <h4 className="absolute inset-x-0 bottom-2  text-center p-2 font-semibold bg-white bg-opacity-70 rounded ">
        {t}
      </h4>
    </div>
  );
}


function Article({article}) {
    const [text, setText] = useState("");

    useEffect(() => {
      fetch(`/articles/${article}.txt`)
        .then(res => res.text())
        .then(text => setText(text));
    }, [article]);

    if (!article) return null;

  return (
  <div className="w-5/6">
    <hr className="border-t border-[#6B6B6B] my-5" />

    <div className="text-sm grid grid-cols-1 gap-5">
    <a className=" font-bold" href={`/articles/${article}.pdf`} target="_blank" rel="noopener noreferrer">{article}</a>
    <a className=" max-w-2xl" href={`/articles/${article}.pdf`} target="_blank" rel="noopener noreferrer">{text}</a>

    </div>

    <hr className="border-t border-[#6B6B6B] mt-5" />

</div>

  )
}




function AoE({ onPopup }) {
  const [isMobile, setIsMobile] = useState(false);
  const [articles, setArticles] = useState([]);

  // Determine screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1250); // mobile breakpoint
    handleResize(); // check on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch Insights only for desktop
  useEffect(() => {
    if (!isMobile) {
      fetch("/articles/allarticles.txt")
        .then(res => res.text())
        .then((arts) => {
          const lines = arts
            .split("\n")
            .map(line => line.trim())
            .filter(line => line.length > 0);
          setArticles(lines);
        })
        .catch(err => {
          console.error("Failed to fetch articles:", err);
          setArticles([]);
        });
    }
  }, [isMobile]);

  const areas = [
{ title: "Subsea Production Control System", image: "/assets/sysarcs.png", text: "• Over 15 years in Subsea Production Control Systems.\n• Specialist in Subsea Hydraulic Control Systems.\n• Broad background with both operators and equipment suppliers, covering system definition, studies, project execution, field operations, and research and development.\n• Extensive knowledge of industry-standard requirements.\n• Strong capability in hydraulic response analysis.\n• Skilled in Root Cause Analysis, Life Extension, and Condition Assessments." },
{ title: "Subsea Workover Control System", image: "/assets/SWCS.jpg", text: "Broad experience with systems including:\n• Intervention and Workover Control Systems (IWOCS)\n• Emergency Disconnect Package (EDP) and Lower Riser Package (LRP)\n• Riserless Light Well Intervention (RLWI) systems\n• Tubing Hanger Installations systems\n• Landing String (LS) systems" },
{ title: "Hydraulic Control Fluids and Cleanliness", image: "/assets/HCFC.png", text: "Independent expert in hydraulic control fluids, with leading expertise in:\n• Qualification programs for new hydraulic control fluids\n• Fluid selection and qualification requirements for new field developments\n• Selection and qualification of replacement control fluids for operational subsea systems\n• Compatibility assessments for control fluids, system materials, and fluid-to-fluid mixing\n\nExpertise in hydraulic cleanliness includes:\n• Defining cleanliness requirements and interpreting international standards\n• Filter sizing, filtration and flushing requirements, and cleanliness verification processes\n• Cleanliness analysis and application of relevant instrumentation" },
{ title: "Systems Engineering and Industry Standards", image: "/assets/SEIS.png", text: "Holds a master’s degree in systems engineering and background with international standards that includes:\n• Over a decade of experience as Global Subject Matter Expert for a major system supplier with the overall responsibility of defining and implementing requirements for Subsea Hydraulic Control Systems.\n• Committee member for API Standard 17F, Subsea Production and Processing Control Systems, 4th and 5th Edition.\n• Task group leader for API Standard 17F, Annex C, Property Requirements of Control Fluids, 4th and 5th Edition.\n• Lead author for the API 17F, 4th Edition, updates to System Pressure Ratings and Performance Requirements." },
  ];

  return (
    <div className="p-4 sm:p-10 w-full flex items-center justify-center" id="aoe-section">

      
      <div className='px-10 inline-flex gap-36'>

    <div>
    <h2 className="text-2xl sm:text-3xl pb-10 italic font-thin mb-6">Areas of Expertise</h2>
      <div className="grid grid-cols-1  sm:grid-cols-2 lg:grid-cols-2 gap-12 gap-y-24">
        {areas.map((area, index) => (
          <div
            key={index}
            className="cursor-pointer"
            onClick={() => onPopup({
              title: area.title,
              area: "Area of Expertise",
              image: area.image,
              text: area.text
            })}
          >
            <AreaEx t={area.title} img_path={area.image} />
          </div>
        ))}
      </div>
    </div>

      {/* Insights only on desktop */}
      {!isMobile && articles.length > 0 && (
        <div className="">
        <h2 className="text-3xl font-thin italic text-[#032866]"><a href="/insights">Insights</a></h2>
            <div className="flex items-center gap-1 mb-4">
            <div className="w-2 h-3 translate-y-1/4 bg-[#004ED1]"></div>
            <h3 className="text-nowrap">see the latest articles</h3>
          </div>
          <div className="grid grid-cols-1 p-2">
            {articles.slice(-3).reverse().map((article, index) => (
              <Article key={index} article={article} />
            ))}
            </div>
          </div>
      )}
      
      </div>
    </div>
  );
}




function Service({ img_path, t }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-1">
      <img src={img_path} alt={t} className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28" />
      <h6 className="text-[#001B49] text-lg sm:text-xl">{t}</h6>
    </div>
  );
}

function Services() {
  return (
    <div className="bg-[#ECF9FF] w-full" id="services">
      <h5 className="font-semibold text-4xl sm:text-5xl text-[#032866] text-center p-6 sm:p-10">
        Services
      </h5>

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-9 w-full px-4 py-5 sm:px-10 lg:px-20 place-items-center">
        <Service img_path="/assets/design.svg" t="System Architecture and design" />
        <Service img_path="/assets/book.svg" t="Technical Assessment and Review" />
        <Service img_path="/assets/magnifying.svg" t="Fault Finding and Root Cause Analysis" />
        <Service img_path="/assets/droplets.svg" t="Control Fluid Selection and Qualification" />
        <Service img_path="/assets/training.svg" t="Training courses" />
        <Service img_path="/assets/checkmark.svg" t="Requirement management" />
      </div>
    </div>
  );
}

function References() {
  const refs = [
    {
      name: "Frederic Place",
      company: "TechnipFMC",
      role: "Lead Project Engineer",
      content: "Subject Matter Expert is one of the many titles Espen has held during his time at FMC Technologies/TechnipFMC and I cannot highlight enough how the word 'Expert' is appropriate enough to describe Espens Competencies and Attributes"
    },
    {
      name: "Goh Peng Kwee",
      company: "Seebird (Singapore)",
      role: "Technical Sales Manager",
      content: "Espen´s enthusiasm and dedication, towards the field of hydraulic system, have inspired many. He possesses the perseverance, competency and innovation to lead the successful Global Hydraulic Network, which comprises of engineers from diverse working backgrounds, cultures and time-zones - a role which very few can handle."
    },
    {
      name: "Ricardo Marques",
      company: "TechnipFMC",
      role: "Manager PCS & IWOCS Service Engineering",
      content: "One of the best! (...) I was truly impressed with his natural ability to engage and understand the topic of safety instrumented systems requirements from IEC61511 and its implication for the hydraulic control systems design"
    },
    {
      name: "Rubem Prandi",
      company: "Optime Subsea",
      role: "Business Development and R&D Engineer",
      content: "Espen is a natural lead, a real team player and, not least, the most skilled hydraulic engineer I´ve come across in oil and gas industry."
    }
  ];

  return (
    <div className="p-6 sm:p-20 flex flex-col sm:flex-row sm:overflow-x-auto gap-6 sm:gap-8">
      {refs.map((ref, index) => (
        <Reference
          key={index}
          name={ref.name}
          company={ref.company}
          role={ref.role}
          content={ref.content}
        />
      ))}
    </div>
  );
}




function Reference({ content, name, role, company }) {
  return (
    <div className="flex-shrink-0 w-full sm:w-[700px] p-6 border-2 rounded-md">
      <p className="text-[#004ED1] text-3xl">❝</p>
      <p className="px-3 text-base sm:text-lg italic break-words">{content}</p>
      <p className="text-[#004ED1] text-end text-3xl">❞</p>

      <div className="text-end mt-3">
        <p className="font-bold text-lg">- {name}</p>
        <p>{role}</p>
        <p>{company}</p>
      </div>
    </div>
  );
}

function Partners() {
  return (
    <div className="bg-white w-full pb-20 px-4 sm:px-20" id="partners">
      <hr className="bg-black" />

      <h5 className="font-semibold text-3xl sm:text-5xl text-[#1f4a94] text-center pt-10 sm:pt-14">
        Business Partners
      </h5>

      <hr className="h-px border-0 bg-gradient-to-r from-transparent via-gray-400 to-transparent my-8" />

      <div className="w-full grayscale">
        
        {/* Top Partners */}
        <div 
          id="top" 
          className="
            flex flex-col sm:flex-row 
            justify-center items-center 
            gap-8 sm:gap-20 
            pt-10
          "
        >
          <a href="https://www.dnv.no" target="_blank" rel="noreferrer">
            <img 
              src="/assets/dnv-logo-cropped_1.png"
              className="
                h-28 sm:h-48 
                w-auto 
                object-contain
              "
              alt="DNV"
            />
          </a>

          <div className="bg-black h-24 sm:h-32 px-8 sm:px-10 flex justify-center items-center">
            <a href="https://www.vedeld.no" target="_blank" rel="noreferrer">
              <img 
                src="/assets/Vedeld.png"
                className="
                  h-8 sm:h-12 
                  w-auto
                  object-contain
                "
                alt="Vedeld"
              />
            </a>
          </div>
        </div>


        {/* Bottom Partners */}
        <div 
          id="bottom"
          className="
            flex flex-col sm:flex-row
            justify-evenly items-center
            gap-10 sm:gap-16
            mt-10
          "
        >
          <a href="https://www.tess.no" target="_blank" rel="noreferrer">
            <img 
              src="/assets/tess-teknisk-faghandel-original_1.jpg"
              className="
                h-16 sm:h-20 
                w-auto
                object-contain
              "
              alt="Tess"
            />
          </a>

          <a href="https://www.ecn.as" target="_blank" rel="noreferrer">
            <img 
              src="/assets/ecn-logo_1.jpg"
              className="
                h-16 sm:h-20
                w-auto
                object-contain
              "
              alt="ECN"
            />
          </a>

          <a href="https://www.k-tech.no" target="_blank" rel="noreferrer">
            <img 
              src="/assets/ktech.png"
              className="
                h-32 sm:h-52
                w-auto
                object-contain
              "
              alt="K-Tech"
            />
          </a>
        </div>

      </div>
    </div>
  );
}

function ClientCard({ client, url }) {
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <div className="inline-block px-1 font-bold text-gray-800 hover:text-gray-900 hover:scale-110 transition-transform duration-300">
        {client}
      </div>
    </a>
  );
}

function Clients() {
  const clients = [
    { name: "Equinor", url: "https://www.equinor.com/no" },
    { name: "TechnipFMC", url: "https://www.technipfmc.com/" },
    { name: "Harbour Energy", url: "https://www.harbourenergy.com" },
    { name: "Vår Energi", url: "https://varenergi.no/no/" },
    { name: "OMV", url: "https://www.omv.no/no" },
    { name: "OKEA", url: "https://okea.no/" },
    { name: "Miljø Norge", url: "https://miljonorge.no/" },
  ];

  return (
    <div className="relative w-full pb-10 bg-[#D2F0FF] px-4 sm:px-20 overflow-hidden">
      <h6 className="font-semibold text-3xl sm:text-5xl text-[#032866] text-center pt-10">
        Clients
      </h6>

      <div className="flex flex-wrap gap-x-5 gap-y-4 w-full justify-center items-center text-xl my-10">
        {clients.map((client, index) => (
          <div key={client.name} className="flex items-center gap-5">
            <ClientCard client={client.name} url={client.url} />

            {index !== clients.length - 1 && (
              <span className="text-gray-800">&bull;</span>
            )}
          </div>
        ))}
      </div>

      <div className="hidden lg:flex justify-between absolute w-full -top-2 pointer-events-none">
        <img
          src="/assets/line1.svg"
          className="px-4 sm:px-20 -translate-x-40 scale-x-[-1]"
          alt="Line"
        />

        <img
          src="/assets/line1.svg"
          className="px-4 sm:px-20"
          alt="Line"
        />
      </div>
    </div>
  );
}


function ContactCard() {
  return (
    <div className="w-full bg-[#F3F3F3] p-6 sm:p-12 flex flex-col sm:flex-row gap-6 sm:gap-12 relative">
      {/* Logo */}
      <img src="/assets/logo.svg" className="w-24 sm:w-32 self-center sm:self-start" alt="Logo" />

      {/* Contact Info */}
      <div className="flex-1">
        <h2 className="font-bold text-xl sm:text-2xl mb-2">Contact Info</h2>
        <p className="text-base sm:text-lg mb-4">
          Espen Mo <br />
          EM Subsea Hydraulics AS<br />
                {/* Org number */}
          <p className=" text-sm sm:text-base font-semibold ">
            Org. No.: 926557211 MVA
          </p>
          <a href="mailto:espen.mo@subseahydraulics.no" className="underline">espen.mo@subseahydraulics.no</a> <br />
          +47 913 63 525
        </p>
        <div className="flex gap-4 sm:gap-6">
          <a href="https://www.linkedin.com/company/em-subsea-hydraulics" target="_blank" rel="noopener noreferrer">
            <img src="/assets/linkdin.svg" className="w-6 sm:w-8" alt="LinkedIn" />
          </a>
          <a href="mailto:espen.mo@subseahydraulics.no">
            <img src="/assets/mail.svg" className="w-6 sm:w-8" alt="Email" />
          </a>
        </div>
      </div>

    </div>
  );
}

function Contact(){
  return(
    <div className='bg-[#0D316D] text-white h-full w-full py-[120px] px-6 lg:px-16 xl:px-32 2xl:px-60'>
      <h1 className='font-bold text-3xl lg:text-4xl'>Contact me</h1>
      <hr className='mt-5 lg:mt-10 border-gray-200 border' />

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 w-full mt-5 lg:mt-14'>

        {/* Left column: details */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 xl:gap-x-16 gap-y-4 content-start'>
          <InputField title="First name" required="true" ID="fName"/>
          <InputField title="Last name" required="true" ID="lName" />
          <InputField title="Email address" required="true" ID="eDress"/>
          <InputField title="Phone Number" ID="pNMR" />

          <div className="md:col-span-2">
            <InputField title="Company" ID="cMpany"/>
          </div>

          <Selector title="Services" placeholder="Choose a category" categories={["System Architecture and design", "Technical Assessment and Review", "Fault Finding and Root Cause Analysis", "Control Fluid Selection and Qualification", "Training Courses", "Requirement Management"]}  ID="servselector"/>
          <Selector title="Areas of Expertise" categories={["Subsea Production Control System", "Subsea Workover Control System", "Hydraulic Control Fluids and Cleanliness", "Systems Engineering and Industry Standards"]} placeholder="Choose a category" ID="aoeselector"/>
        </div>

        {/* Right column: comments */}
        <div className='min-h-[14rem]'>
          <InputField title="Comments/Questions" required full ID="cQues"/>
        </div>
      </div>

      <div className='text-lg justify-center flex text-center pt-4 text-[#F30000] font-bold hidden' id="contact_message"></div>

      <div className=' grid grid-cols-1 w-full justify-center items-center mt-16 text-center md:flex gap-4 md:gap-14 md:mt-20'>
        <Button title="SUBMIT" functi={submitContact} />
        <Button title="CANCEL" inverted functi={clearContact}/>
      </div>
    </div>
  )
}

async function submitContact() {
    const message = document.getElementById("contact_message");
    const submitBtn = document.getElementById("submitButton");

    // Reset message
    message.classList.add("hidden");
    message.textContent = "";

    // Get values
    const fname = document.getElementById("fName").value.trim();
    const lname = document.getElementById("lName").value.trim();
    const eaddress = document.getElementById("eDress").value.trim();
    const pnumber = document.getElementById("pNMR").value.trim();
    const cmpny = document.getElementById("cMpany").value.trim();
    const qstion = document.getElementById("cQues").value.trim();
    const servcs = document.getElementById("servselector").value;
    const aoexp = document.getElementById("aoeselector").value;

    // Required fields
    if (!fname || !lname || !eaddress) {
        message.classList.remove("hidden");
        message.textContent = "Please fill out all required fields.";
        return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(eaddress)) {
        message.classList.remove("hidden");
        message.textContent = "Please enter a valid email address.";
        return;
    }

    const data = {
        first_name: fname,
        last_name: lname,
        email_address: eaddress,
        phone_number: pnumber,
        company: cmpny,
        services: servcs,
        area_of_expertise: aoexp,
        question: qstion
    };

 

    try {
        const response = await fetch("https://subseahydraulics.no/sendmail", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`);
        }

        message.classList.remove("hidden");
        message.textContent = "Message successfully sent!";

        // Clear the form
        clearContact();

    } catch (error) {
        console.error(error);

        message.classList.remove("hidden");
        message.textContent =
            "Something went wrong. Please try again later or contact us through another method.";
    } 
}


function clearContact(){
  document.getElementById("fName").value = "";
  document.getElementById("lName").value = "";
  document.getElementById("eDress").value= "";
  document.getElementById("pNMR").value  = "";
  document.getElementById("cMpany").value= "";
  document.getElementById("cQues").value = "";
  document.getElementById("servselector").value = "";
  document.getElementById("aoeselector").value = "";
}

function InputField({title, placeholder, required, full, ID}){
  return(
    <div className={`w-full min-w-0 ${full ? 'h-full flex flex-col' : ''}`}>
      <h2 className='font-semibold ml-6 text-lg'>
        {required ?  <span className='text-[#F30000]'>* </span> : <></> }
        {title}
      </h2>
      { full ?
        <textarea name="" id={ID} className='p-1 resize-none text-black text-lg w-full mt-2 flex-1 min-h-[12rem] align-top'></textarea>
        : <input id={ID} className='p-1 text-black text-lg w-full mt-2' type="text" placeholder={placeholder} />
      }
    </div>
  )
}

function Selector({title, categories ,placeholder, ID}){
  return(
    <div>
      <h2 className='font-semibold ml-6 text-lg' >{title}</h2>
      <select className='text-black w-full p-3  border-b-[#F30000] border-2' name="" id={ID}>
        <option defaultValue={""} value="">{placeholder}</option>
        {categories.map((category, index) => ( 
          <option value={category}> {category}</option>
        ))}
      </select>
    </div>
  )
}

function Button({title, inverted, functi}){
  return(
    <div>
      { inverted ?
        <button className='text-xl text-white font-semibold px-[75px] py-3 bg-[#0D316D] border-[#F5F5F5] border-2 transition-all duration-300 hover:bg-[#001B49] hover:-translate-y-1 hover:shadow-md' onClick={functi}>{title}</button> :
        <button className='text-xl text-[#0D316D] font-semibold px-[75px] py-3 bg-white border-[#F5F5F5] border-2 transition-all duration-300 hover:bg-[#ffffff] hover:-translate-y-1 hover:shadow-md 'onClick={functi} >{title}</button>
      }
    </div>
  )
}

function Insight({insight_name}){

    const [text, setText] = useState("");

    useEffect(() => {
      fetch(`/articles/${insight_name}.txt`)
        .then(res => res.text())
        .then(text => setText(text));
    }, [insight_name]);

    if (!insight_name) return null;

  return(
    <div className='relative'>

    <div className=' relative  w-80 h-[26rem] flex flex-col p-5 bg-white z-10 border-2 transform transition-transform ease-out duration-500 hover:-translate-x-1 hover:-translate-y-1'>
      <header className='w-full'> 
      <h2 className='text-2xl font-semibold font-sans mt-2'> {insight_name} </h2>
      <p className=' translate-x-60 font-semibold text-white -rotate-90 bg-[#0D316D] text-center text-sm w-fit px-4 '> ARTICLE </p>
      </header>
      
      <p className='text-xs max-h-72 overflow-y-auto font-sans max-w-56 py-2'>{text}</p>
      
      <a
        href={`/articles/${insight_name}.pdf`}
        className="text-[#004ED1] font-bold text-sm text-left inline-block"
      >
        <span className="mr-2">&gt;</span> Read more
      </a>      
    </div>

    <div className='absolute inset-5 w-80 h-[26rem] bg-[#0D316D] z-0'></div>
    </div>
  )
}

function Insights() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetch("/articles/allarticles.txt")
      .then((res) => res.text())
      .then((arts) => {
        // Split into lines, trim, and filter out empty ones
        const lines = arts
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0); // ignore empty strings

        setArticles(lines);
      })
      .catch((err) => {
        console.error("Failed to fetch articles:", err);
        setArticles([]); // fallback to empty array
      });
  }, []);

  return (
    <div className="relative pb-32 overflow-hidden">
      <div className="relative pt-36 z-10 w-full grid grid-cols md:grid-cols-2 lg:grid-cols-3 gap-y-20 place-items-center lg:px-20">
        {articles.map((article, index) => (
          <Insight
            key={index}
            insight_name={article || "Untitled"} // fallback in case something is empty
          />
        ))}
      </div>

      <div className="absolute inset-0 -top-32 z-0 w-[150vw] aspect-square translate-y-0 bg-[#03286621] [clip-path:polygon(0_0,100%_50%,0_100%)]" />
      <div className="absolute inset-0 -top-32 z-0 w-[135vw] aspect-square translate-y-24 bg-[#03286621] [clip-path:polygon(0_0,100%_50%,0_100%)]" />
      <div className="absolute inset-0 -top-32 z-0 w-[120vw] aspect-square translate-y-52 bg-[#03286621] [clip-path:polygon(0_0,100%_50%,0_100%)]" />
    </div>
  );
}

function AoE_popup({ Title, Area_of_exp, image_path, text }) {
  return (
    <div className="flex flex-col gap-3 max-h-[90vh] overflow-auto overflow-x-hidden">
      <h2 className="text-3xl font-bold text-[#0d1d3a]">{Title}</h2>
      <h3 className="text-xl text-[#004ED1] font-bold">{Area_of_exp}</h3>

      <div className="my-6 flex flex-col gap-2 text-left">
        {text.split("\n").map((line, i) => {
          const trimmed = line.trim();

          // blank line = spacer between groups
          if (!trimmed) return <div key={i} className="h-3" />;

          // bullet line
          if (trimmed.startsWith("•")) {
            return (
              <div key={i} className="flex gap-3 pl-2 leading-snug">
                <span className="text-[#004ED1]">•</span>
                <span>{trimmed.slice(1).trim()}</span>
              </div>
            );
          }

          // intro / heading line
          return (
            <p key={i} className="leading-snug font-semibold">
              {trimmed}
            </p>
          );
        })}
      </div>

      <img src={image_path} alt="" className="w-full max-w-4xl rounded-sm object-cover hidden sm:block" />
    </div>
  )
}


function App() {
  const [activePopup, setActivePopup] = useState(null);
  const [animatePopup, setAnimatePopup] = useState(false);

  useEffect(() => {
    document.body.style.overflow = activePopup ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [activePopup]);

  useEffect(() => {
    if (activePopup) {
      setTimeout(() => setAnimatePopup(true), 10);
    } else {
      setAnimatePopup(false);
    }
  }, [activePopup]);

  return (
    <div className='overflow-x-hidden'>
    <Router>
        <ScrollToHash />
      <Routes>
        {/* Route to show only Contact page And Insights */}
        <Route path="/contact" element={<> <Header_stranger /> <Contact /> <ContactCard /> </>} />
        <Route path="/insights" element={<> <Header_stranger/> <Insights/> <ContactCard /> </>} />
        {/* Route to show full site */}
        <Route
          path="/"
          element={
            <>
              {/* Blur wrapper */}
                <div className={activePopup ? "blur-sm pointer-events-none" : ""}>
                <Header />
                <Banner />
                <AoE onPopup={setActivePopup} />
                <Services />
                <Partners />
                <Clients />
                <References />
                <ContactCard />
              </div>
              {/* Popup overlay */}

              
              {activePopup && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-50">
              <div className={`relative bg-white transition-all duration-300 rounded-lg shadow-lg p-8 max-w-3xl w-full ${animatePopup ? "scale-y-100 opacity-100" : "scale-y-0 opacity-0"}`}>
              <AoE_popup 
              Title={activePopup.title}
              Area_of_exp={activePopup.area}
              image_path={activePopup.image}
              text={activePopup.text}
              />

              <button
              className="absolute top-4 right-4 text-2xl font-bold"
              onClick={() => setActivePopup(null)}
              >
              ×
              </button>
              </div>
              </div>
              )}


            </>
          }
          />

      </Routes>
    </Router>
    </div>
  );
}

export default App;
